/** args["tour_tags"] ⇄ TagConditionValue for the tpl-45 editor (CP-8264).
 *
 * Rule args round-trip verbatim through the API — unlike blacklist rows there
 * is no server-side chip resolution on read — so pinned chips persist their
 * display labels alongside the id (the same precedent as `args.tours` storing
 * whole tour objects). The backend evaluator reads only `tag.id` (or a bare
 * int) and `category_code`+`tag_name`; everything else is display data.
 */
import { isCanonicalSpec, type TagChip, type TagConditionValue } from '@/components/TagConditionInput';

export interface ArgsPinnedTag {
  id: number;
  name?: string;
  category?: { code?: string; name?: string };
  tenant?: { code?: string } | null;
}

export type ArgsTagChip =
  | { tag: ArgsPinnedTag | number }
  | { category_code: string; tag_name: string }
  | { category_code: string; canonical: string };

export interface ArgsTourTags {
  match_mode: 'and' | 'or';
  tags: ArgsTagChip[];
}

/** Parse whatever is stored under args["tour_tags"] — defensively: rule args
 *  are free-form JSON and old/foreign writers may have stored anything. */
export function tourTagsFromArgs(raw: unknown): TagConditionValue {
  const chips: TagChip[] = [];
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const entries = Array.isArray(obj.tags) ? obj.tags : [];
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if ('tag' in e) {
      const t = e.tag;
      const pin = t && typeof t === 'object' ? (t as ArgsPinnedTag) : null;
      const id = typeof t === 'number' ? t : pin ? Number(pin.id) : NaN;
      if (!Number.isFinite(id)) continue;
      chips.push({
        spec: { tag_id: id },
        labels: {
          tenant: pin?.tenant?.code ?? null,
          category: pin?.category?.name ?? pin?.category?.code ?? '?',
          tag: pin?.name ?? `#${id}`,
        },
      });
    } else if (typeof e.category_code === 'string' && typeof e.canonical === 'string') {
      chips.push({
        spec: { category_code: e.category_code, canonical: e.canonical },
        labels: { tenant: null, category: e.category_code, tag: `~${e.canonical}` },
      });
    } else if (typeof e.category_code === 'string' && typeof e.tag_name === 'string') {
      chips.push({
        spec: { category_code: e.category_code, tag_name: e.tag_name },
        labels: { tenant: null, category: e.category_code, tag: e.tag_name },
      });
    }
  }
  return { matchMode: obj.match_mode === 'or' ? 'or' : 'and', chips };
}

/** Widget value → the args shape (labels persisted for the next editor load). */
export function tourTagsToArgs(value: TagConditionValue): ArgsTourTags {
  return {
    match_mode: value.matchMode,
    tags: value.chips.map((c) => {
      if ('tag_id' in c.spec) {
        return {
          tag: {
            id: c.spec.tag_id,
            name: c.labels.tag,
            category: { name: c.labels.category },
            tenant: c.labels.tenant ? { code: c.labels.tenant } : null,
          },
        };
      }
      if (isCanonicalSpec(c.spec)) {
        return { category_code: c.spec.category_code, canonical: c.spec.canonical };
      }
      return { category_code: c.spec.category_code, tag_name: c.spec.tag_name };
    }),
  };
}
